/* eslint-disable react/jsx-filename-extension */
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import ToastActions from 'actions/ToastActions';
import CurrentUser from 'auth/CurrentUser';
import Button from 'components/ButtonDeprecated';
import Columns2 from 'components/Columns2';
import Fieldset from 'components/Fieldset';
import FormDeprecated from 'components/FormDeprecated';
import FormInput from 'components/FormInput';
import Link from 'components/Link';
import FormSelect from 'components/FormSelect';
import Text from 'components/Text';
// @ts-ignore
import User from 'entities/User';
import { Container } from 'flux/utils';
import GroupActions from 'groups/actions/GroupActions';
import * as React from 'react';
import SettingsStore from 'stores/SettingsStore';
// @ts-ignore
import UserActions from 'user/UserActions';
import lockSVG from '../../assets/icons/lock-small.svg';
// @ts-ignore
import SettingsContainer from './SettingsContainer';

type State = {
  user?: User;
  canSubmit: boolean;
  hasChanged: boolean;
  countries: Array<any>;
};

type Props = { data: { me: { permissions: { canChangeName: boolean } } } };

class UserPersonalInfoForm extends React.Component<Props, State> {
  static getStores() {
    return [SettingsStore, CurrentUser];
  }

  static calculateState() {
    return {
      user: CurrentUser.isSignedIn() ? CurrentUser.getUser() : null,
      canSubmit: false,
      hasChanged: false,
      countries: SettingsStore.getSetting('countries'),
    };
  }

  disableButton = () => {
    this.setState({ canSubmit: false });
  };

  enableButton = () => {
    if (this.state.hasChanged) {
      this.setState({ canSubmit: true });
    }
  };

  enableChanged = () => {
    this.setState({ hasChanged: true });
  };

  submit = (attrs: any, resetForm: any, invalidateForm: any) => {
    // When the user changes country we reload all the user classes.
    // This is because the class subscription info changes depending on the user country.
    if (attrs['country_code'] !== CurrentUser.getUser().getCountryCode()) {
      GroupActions.loadGroups();
    }

    const emailChanged = attrs.email && attrs.email !== CurrentUser.getUser().getEmail();

    const reset = () => {
      this.disableButton();
      this.setState({ hasChanged: false });
    };

    const onSuccess = () => {
      ToastActions.showSuccessToast(
        emailChanged ? 'Changes saved. Please verify your new email address.' : 'Changes saved'
      );
    };

    const onFailure = (error: any) => {
      ToastActions.showAPIError(error);

      const errors: { [key: string]: string } = {};
      for (const field of error.field_errors) {
        errors[field['field_name']] = field.messages.join(', ');
      }

      invalidateForm(errors);
    };

    UserActions.update(attrs)
      .then(reset)
      .then(onSuccess, onFailure);
  };

  render() {
    const { data } = this.props;

    const user = this.state.user;
    if (!user || !data) {
      return null;
    }

    let language = user.getLocale();

    // API accepts both en and en_US, but en-US is correct format
    if (language === 'en') {
      language = 'en-US';
    }

    const canChangeName = data.me?.permissions?.canChangeName;

    return (
      <FormDeprecated
        key={user.cid}
        onChange={this.enableChanged}
        onValid={this.enableButton}
        onValidSubmit={this.submit}
        onInvalid={this.disableButton}>
        <Fieldset legend="Personal information">
          <SettingsContainer>
            <Columns2 gutter="small" className={!canChangeName ? 'Columns2Name' : undefined}>
              <div>
                <FormInput
                  label="First name"
                  name="first_name"
                  value={user.getFirstName()}
                  validations="minLength: 1"
                  validationError="First name is required"
                  disabled={!canChangeName}
                />
              </div>
              <div>
                <FormInput
                  label="Last name"
                  name="last_name"
                  value={user.getLastName()}
                  validations="minLength:1"
                  validationError="Last name is required"
                  disabled={!canChangeName}
                />
              </div>
            </Columns2>

            {!canChangeName && (
              <div className="disabled-change-names">
                <img src={lockSVG} alt="lock-icon" />
                <Text size="xsmall" color="graphite">
                  Name editing has been disabled by a Remind administrator.{' '}
                  <Link
                    href="https://help.remind.com/hc/en-us/articles/360059428272"
                    target="_blank">
                    Learn more
                  </Link>
                </Text>
              </div>
            )}

            <Columns2 gutter="small">
              <div>
                <FormSelect
                  label="Country"
                  name="country_code"
                  options={this.state.countries.map(country => {
                    return { value: country.code, label: country.name };
                  })}
                  value={user.getCountryCode()}
                />
              </div>
            </Columns2>
          </SettingsContainer>

          <Button size="small" type="submit" disabled={!this.state.canSubmit}>
            Save
          </Button>
        </Fieldset>
      </FormDeprecated>
    );
  }
}

const withQuery = graphql(gql`
  query TestChangeName {
    me {
      uuid
      permissions {
        canChangeName
      }
    }
  }
`);

const UserPersonalInfoFormContainer = Container.create(UserPersonalInfoForm as any);

export { UserPersonalInfoFormContainer };
export default withQuery(Container.create(UserPersonalInfoForm as any));
